/**
 * The person's recently opened work (projects for now), shared by the web sidebar
 * and the native Projects tab. Stored in user_preferences.prefs.recent so it
 * follows them across devices and both apps; written whole through
 * merge_user_preferences, so two devices race last-writer-wins on the list,
 * which is acceptable for a convenience list.
 */

export type RecentKind = 'project';

export interface RecentItem {
  kind: RecentKind;
  id: string;
  /** Name when last opened; callers prefer the live name when they have it. */
  title: string;
  /** The project section that was open, so a click lands where they left off. */
  section?: string;
  openedAt: string;
}

/** How many are kept; the surfaces show fewer. */
export const RECENT_STORED_LIMIT = 8;
const MAX_ID_LENGTH = 128;
const MAX_TITLE_LENGTH = 160;
const MAX_SECTION_LENGTH = 64;

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function boundedString(value: unknown, max: number): string | null {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= max ? value : null;
}

const keyOf = (item: Pick<RecentItem, 'kind' | 'id'>) => `${item.kind}:${item.id}`;

/** Any stored value to a clean list: bad entries dropped, duplicates collapsed, newest first, capped. */
export function normaliseRecent(raw: unknown): RecentItem[] {
  const items = asRecord(raw).items;
  if (!Array.isArray(items)) return [];
  const out: RecentItem[] = [];
  const seen = new Set<string>();
  for (const entry of items) {
    const row = asRecord(entry);
    const id = boundedString(row.id, MAX_ID_LENGTH);
    const title = boundedString(row.title, MAX_TITLE_LENGTH);
    const openedAt = typeof row.openedAt === 'string' && Number.isFinite(Date.parse(row.openedAt)) ? row.openedAt : null;
    if (row.kind !== 'project' || !id || !title || !openedAt) continue;
    const item: RecentItem = { kind: 'project', id, title, openedAt };
    const section = boundedString(row.section, MAX_SECTION_LENGTH);
    if (section) item.section = section;
    if (seen.has(keyOf(item))) continue;
    seen.add(keyOf(item));
    out.push(item);
  }
  out.sort((a, b) => Date.parse(b.openedAt) - Date.parse(a.openedAt));
  return out.slice(0, RECENT_STORED_LIMIT);
}

/**
 * Move an opened item to the front. Returns the same array when nothing a
 * person would see changed (already first, same title and section), so callers
 * can skip the write when someone hops between tabs of the job they are on.
 */
export function recordRecent(
  list: RecentItem[],
  opened: Omit<RecentItem, 'openedAt'>,
  now: Date = new Date(),
): RecentItem[] {
  const title = opened.title.trim().slice(0, MAX_TITLE_LENGTH);
  if (!opened.id || !title || opened.id.length > MAX_ID_LENGTH) return list;
  const section = opened.section && opened.section.length <= MAX_SECTION_LENGTH ? opened.section : undefined;
  const first = list[0];
  if (first && keyOf(first) === keyOf(opened) && first.title === title && first.section === section) return list;
  const next: RecentItem = { kind: opened.kind, id: opened.id, title, openedAt: now.toISOString() };
  if (section) next.section = section;
  return [next, ...list.filter((item) => keyOf(item) !== keyOf(opened))].slice(0, RECENT_STORED_LIMIT);
}

/** Drop a stale entry (deleted project, access removed) without touching the rest. */
export function forgetRecent(list: RecentItem[], target: Pick<RecentItem, 'kind' | 'id'>): RecentItem[] {
  const next = list.filter((item) => keyOf(item) !== keyOf(target));
  return next.length === list.length ? list : next;
}

export interface VisibleRecentOptions {
  limit: number;
  /** Live name for an id, or undefined when the project is not known (yet). */
  liveTitle?: (item: RecentItem) => string | undefined;
  /** False hides an entry the person can no longer open; undefined means not known yet, so keep it. */
  isAvailable?: (item: RecentItem) => boolean | undefined;
}

/** What a surface shows: available entries, live names, at most `limit`. */
export function visibleRecent(list: RecentItem[], options: VisibleRecentOptions): RecentItem[] {
  const out: RecentItem[] = [];
  for (const item of list) {
    if (out.length >= options.limit) break;
    if (options.isAvailable?.(item) === false) continue;
    const title = options.liveTitle?.(item)?.trim();
    out.push(title && title !== item.title ? { ...item, title } : item);
  }
  return out;
}

/** Two-letter mark for a project tile: initials of the first two words, or the first two letters. */
export function recentMonogram(title: string): string {
  // Cased letters and digits, without Unicode property escapes the native engine may lack.
  const isMark = (ch: string) => /[0-9]/.test(ch) || ch.toLowerCase() !== ch.toUpperCase();
  const marks = (word: string) => [...word].filter(isMark);
  const words = title.trim().split(/\s+/).map(marks).filter((chars) => chars.length > 0);
  if (words.length === 0) return '?';
  const mark = words.length >= 2 ? words[0][0] + words[1][0] : words[0].slice(0, 2).join('');
  return mark.toLocaleUpperCase('en-GB');
}

/* ── Storage ──────────────────────────────────────────────────────────────── */

interface BackendResult<T> {
  data: T | null;
  error: { code?: string; message?: string } | null;
}

/** The two calls both apps' Supabase clients answer; typed by hand because the generated types predate the table. */
export interface RecentClient {
  from(table: 'user_preferences'): {
    select(columns: 'prefs'): {
      eq(column: 'user_id', value: string): { maybeSingle(): PromiseLike<BackendResult<{ prefs: unknown }>> };
    };
  };
  rpc(fn: 'merge_user_preferences', args: { patch: { recent: { items: RecentItem[] } } }): PromiseLike<BackendResult<unknown>>;
}

/** The stored list, or null when it could not be read (keep whatever is on screen). */
export async function fetchRecent(client: RecentClient, userId: string): Promise<RecentItem[] | null> {
  const { data, error } = await client.from('user_preferences').select('prefs').eq('user_id', userId).maybeSingle();
  if (error) return null;
  return normaliseRecent(asRecord(data?.prefs).recent);
}

/** Replace the stored list. A failure is reported, not thrown: losing one visit is not worth an error. */
export async function saveRecent(client: RecentClient, items: RecentItem[]): Promise<boolean> {
  const { error } = await client.rpc('merge_user_preferences', { patch: { recent: { items } } });
  return !error;
}
